import { can, type AuthorizedUser } from '@dma/auth';
import { AuthorizationError, ApplicationError, ValidationError, type Logger } from '@dma/shared';
import { validateImageFile } from '../../domain/rules/validateImageFile';
import { InvalidImageFileError } from '../../domain/errors/InvalidImageFileError';
import type { MediaRepository } from '../../domain/repositories/MediaRepository.interface';

/**
 * Gated at the baseline `admin:access` permission — the same one that lets a
 * signed-in user reach the admin panel at all — rather than a specific
 * content permission like `news:create` or `settings:update`. Uploading a
 * file doesn't itself publish or change any content; the URL it returns
 * only takes effect once attached to an article/banner/scientific-entry and
 * saved through that content's own permission-gated Save use case. Storage
 * RLS (see infra/supabase/policies/007_site_media_storage_policies.sql)
 * still separately requires super_admin/editor at the database level — this
 * check is the app-level defense-in-depth layer, not the only one.
 */
export class UploadImageUseCase {
  constructor(
    private readonly repository: MediaRepository,
    private readonly logger: Logger
  ) {}

  public async execute(actingUser: AuthorizedUser | null, file: File, folder: string): Promise<string> {
    if (!can(actingUser, 'admin:access')) {
      throw new AuthorizationError();
    }

    try {
      validateImageFile(file);
    } catch (error) {
      if (error instanceof InvalidImageFileError) {
        throw new ValidationError(error.message);
      }
      throw error;
    }

    try {
      return await this.repository.upload(file, folder);
    } catch (error) {
      this.logger.error('Failed to upload image', {
        folder,
        fileName: file.name,
        cause: error instanceof Error ? error.message : String(error)
      });
      throw new ApplicationError('IMAGE_UPLOAD_FAILED', 'آپلود تصویر با مشکل مواجه شد. لطفاً دوباره تلاش کنید.', {
        cause: error
      });
    }
  }
}
